// =============================================================================
// Prospect NDA Sign Page (Phase II)
// =============================================================================

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { fundraisingService } from '../../services/fundraising.service';
import { getApiError } from '../../services/api';
import type { ProspectNdaStatus } from '../../types/fundraising';

// =============================================================================
// Component
// =============================================================================

export function NdaSignPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [ndaStatus, setNdaStatus] = useState<ProspectNdaStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [signatureMethod, setSignatureMethod] = useState<'TYPE' | 'DRAW'>('TYPE');
  const [signedName, setSignedName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const loadNdaStatus = useCallback(async () => {
    if (!dealId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getProspectNdaStatus(dealId);
      setNdaStatus(data);

      // If already signed, redirect to documents
      if (data.ndaSignedAt) {
        navigate(`/prospect/datarooms/${dealId}`);
      }
    } catch (err) {
      console.error('Failed to load NDA status:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [dealId, navigate]);

  useEffect(() => {
    loadNdaStatus();
  }, [loadNdaStatus]);

  // Canvas drawing handlers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getSignatureData = (): string | undefined => {
    if (signatureMethod !== 'DRAW') return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    if (!dealId || !signedName || !agreedToTerms) return;

    setIsSigning(true);
    setError(null);
    try {
      await fundraisingService.signProspectNda(dealId, {
        signatureData: getSignatureData(),
        signatureMethod,
        signedName,
        agreedToTerms: true,
      });

      // Navigate to documents
      navigate(`/prospect/datarooms/${dealId}`);
    } catch (err) {
      console.error('Failed to sign NDA:', err);
      setError(getApiError(err).message);
    } finally {
      setIsSigning(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!ndaStatus?.ndaRequired) {
    navigate(`/prospect/datarooms/${dealId}`);
    return null;
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/prospect/datarooms')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Non-Disclosure Agreement</h1>
          <p className="text-sm text-gray-500 mt-1">
            Please sign the NDA to access {ndaStatus.dealName} data room
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* NDA Content */}
      <Card>
        <div className="prose prose-sm max-w-none">
          <h3>Non-Disclosure Agreement</h3>
          <p>
            By signing this Non-Disclosure Agreement, you agree to keep confidential all
            information, documents, and materials shared with you through the {ndaStatus.dealName} data room.
          </p>
          <p>You agree to:</p>
          <ul>
            <li>Keep all information strictly confidential</li>
            <li>Not share, copy, or distribute any materials to third parties</li>
            <li>Use the information solely for evaluating the investment opportunity</li>
            <li>Return or destroy all materials upon request</li>
            <li>Not use any information for competitive purposes</li>
          </ul>
          <p>
            This agreement shall remain in effect for a period of two (2) years from the date
            of signature, regardless of whether you decide to invest.
          </p>
        </div>
      </Card>

      {/* Signature Section */}
      <Card>
        <h3 className="font-medium text-gray-900 mb-4">Your Signature</h3>

        {/* Signature Method Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSignatureMethod('TYPE')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              signatureMethod === 'TYPE'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Type Name
          </button>
          <button
            type="button"
            onClick={() => setSignatureMethod('DRAW')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              signatureMethod === 'DRAW'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Draw Signature
          </button>
        </div>

        {/* Typed Signature */}
        {signatureMethod === 'TYPE' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Legal Name
            </label>
            <input
              type="text"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder="Enter your full legal name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {signedName && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Signature Preview</p>
                <p
                  className="text-2xl italic"
                  style={{ fontFamily: 'cursive, serif', color: '#1e3a5f' }}
                >
                  {signedName}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Drawn Signature */}
        {signatureMethod === 'DRAW' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Draw your signature
              </label>
              <button
                type="button"
                onClick={clearCanvas}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={500}
                height={150}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="w-full cursor-crosshair"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Use your mouse to draw your signature above
            </p>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Print Name
              </label>
              <input
                type="text"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="Enter your full legal name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Agreement Checkbox */}
      <Card>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
          />
          <span className="text-sm text-gray-700">
            I have read and understand the Non-Disclosure Agreement. I agree to be bound by
            its terms and conditions. I understand that my electronic signature has the same
            legal effect as a handwritten signature.
          </span>
        </label>
      </Card>

      {/* Sign Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ShieldCheckIcon className="h-5 w-5" />
          <span>Your signature is secured and legally binding</span>
        </div>
        <Button
          onClick={handleSign}
          disabled={!signedName || !agreedToTerms || isSigning}
          className="flex items-center gap-2"
        >
          {isSigning ? (
            'Signing...'
          ) : (
            <>
              <DocumentCheckIcon className="h-5 w-5" />
              Sign & Continue
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default NdaSignPage;
